import { QuizType } from '@/redux/reducers/quizzes/quizSlice';
import React, { useCallback, useMemo } from 'react';
import { FormQuiz } from '../FormQuiz';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getLibraryQuizV2 } from '@/features/Quiz/api/libraryQuizDetailV2';
import type { LibraryQuizDetailsV2 } from '@/features/Quiz/api/libraryQuizDetailV2';
import {
    getAdminQuizById,
    getAdminQuizDetails,
    getQuiz,
    getQuizLibraryById,
    getQuizTemplateByArticleId,
    getUserQuizById,
} from '@/features/Quiz/api/getQuizzes';
import { isQuizUuid, libraryQuizDetailsToAdminContent, getLibraryQuizV2QueryKey } from '@/features/Quiz/utils/quizV2Library';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ErrorBlock } from '@/components/ErrorBlock';
import { useAuth } from '@/context/AuthContext';
import { mapAdminQuizDetailsToQuizCopy } from '@/features/Quiz/utils/quizV2AdminEdit';
import type { AdminQuizDetailsContent } from '@/features/Quiz/utils/quizV2AdminEdit';

type Props = {
    isLibrary?: boolean;
    isAdminQuiz?: boolean;
    /** Admin quiz create via quiz-svc (POST /api/v2/admin/quiz/create). */
    isAdminQuizV2?: boolean;
    /** My Quizzes (current user) create via quiz-svc (POST /api/v2/admin/quiz/create). */
    isMyQuizV2?: boolean;
    isUserQuiz?: boolean;
    /** Library quiz CRUD via quiz-svc V2 APIs when OpenSearch library flag is on. */
    libraryQuizV2Api?: boolean;
};

export const CreateQuiz = ({
    isLibrary = false,
    isAdminQuiz = false,
    isAdminQuizV2 = false,
    isMyQuizV2 = false,
    isUserQuiz = false,
    libraryQuizV2Api = false,
}: Props) => {
    const { user } = useAuth();
    const isSuperAdmin = user?.roles.includes('ROLE_SUPER_ADMIN');

    const { articleId, quizId, quizLibraryId, adminQuizId, userQuizId, quizUuid } = useParams();
    const [searchParams] = useSearchParams();

    // Detect UUID by shape so we never Number(uuid) → NaN; gate V2 fetch on the flag.
    const routeLibraryUuid = [quizLibraryId, quizId].find(isQuizUuid);
    const libraryV2Uuid = libraryQuizV2Api ? routeLibraryUuid : undefined;
    const isArticleLibraryV2Copy = !!libraryQuizV2Api && isQuizUuid(quizId);
    const isLibraryQuizV2 = (isLibrary && libraryQuizV2Api) || isArticleLibraryV2Copy;
    const effectiveIsLibrary = isLibrary || isLibraryQuizV2;

    const defaultLibraryType: 'TEXTBOOK' | 'CHAPTER' | 'VIDEO' = (() => {
        const param = searchParams.get('libraryType');
        if (param === 'CHAPTER') return 'CHAPTER';
        if (param === 'VIDEO') return 'VIDEO';
        return 'TEXTBOOK';
    })();

    const quizQueryKey = useCallback(() => {
        if (isAdminQuizV2 && quizUuid) {
            return `getAdminQuizDetails-copy-${quizUuid}`;
        }
        if (adminQuizId) {
            return `getAdminQuiz-${adminQuizId}`;
        }

        if (userQuizId) {
            return `getUserQuiz-${userQuizId}`;
        }

        if (libraryV2Uuid) {
            return getLibraryQuizV2QueryKey(libraryV2Uuid);
        }

        if (quizLibraryId) {
            return `getQuizLibrary-${quizLibraryId}`;
        }

        if (articleId) {
            return `getQuizArticle-${articleId}`;
        }

        return `getQuiz-${quizId}`;
    }, [
        isAdminQuizV2,
        quizUuid,
        adminQuizId,
        quizLibraryId,
        quizId,
        articleId,
        userQuizId,
        libraryV2Uuid,
    ]);

    const { data, isLoading, isError, error } = useQuery(
        quizQueryKey(),
        async () => {
            if (isAdminQuizV2 && quizUuid) {
                return await getAdminQuizDetails(quizUuid);
            }
            if (quizId || quizLibraryId || adminQuizId || articleId || userQuizId) {
                if (userQuizId) {
                    return await getUserQuizById(Number(userQuizId));
                }
                if (adminQuizId) {
                    return await getAdminQuizById(Number(adminQuizId));
                }
                if (libraryV2Uuid) {
                    return await getLibraryQuizV2(libraryV2Uuid);
                }
                if (quizLibraryId && !isQuizUuid(quizLibraryId)) {
                    return await getQuizLibraryById(Number(quizLibraryId));
                }
                if (articleId) {
                    const action = await getQuizTemplateByArticleId(Number(articleId));

                    return {
                        id: 0,
                        article: Number(articleId),
                        title: '',
                        active: false,
                        startDate: '',
                        endDate: '',
                        duration: 0,
                        countOfQuestions: 0,
                        editable: false,
                        activities: action.activities,
                        tags: [],
                        createdAt: '',
                        updatedAt: null,
                    };
                }

                if (quizId && !isQuizUuid(quizId)) {
                    return await getQuiz(Number(quizId));
                }
                return null;
            }

            return null;
        },
        {
            enabled: !(routeLibraryUuid && !libraryQuizV2Api),
            useErrorBoundary: (error: any) => error.response?.status >= 400,
        }
    );

    const mappedQuiz = useMemo(() => {
        if (isAdminQuizV2 && quizUuid && data) {
            return mapAdminQuizDetailsToQuizCopy(data as AdminQuizDetailsContent);
        }
        if (isLibraryQuizV2 && libraryV2Uuid && data) {
            return mapAdminQuizDetailsToQuizCopy(
                libraryQuizDetailsToAdminContent(data as LibraryQuizDetailsV2)
            );
        }
        return (data as QuizType) ?? null;
    }, [isAdminQuizV2, isLibraryQuizV2, quizUuid, libraryV2Uuid, data]);

    const libraryQuizV2Details =
        isLibraryQuizV2 && libraryV2Uuid && data ? (data as LibraryQuizDetailsV2) : null;

    if (!isSuperAdmin && !effectiveIsLibrary && !isAdminQuiz && !isUserQuiz && !isMyQuizV2) {
        return <Navigate to="/"/>
    }

    // UUID in the URL but V2 library API is off — never call Number(uuid)
    if (routeLibraryUuid && !libraryQuizV2Api) {
        return <ErrorBlock error={'Error'} textOnly />;
    }

    if (isLoading) return <LoadingSpinner isFullScreen />;
    if (error?.statusCode === 404 || isError) return <ErrorBlock error={'Error'} textOnly />;

    return (
        <FormQuiz
            quiz={mappedQuiz ?? null}
            isLibrary={effectiveIsLibrary}
            isLibraryQuizV2={isLibraryQuizV2}
            libraryQuizV2Details={libraryQuizV2Details}
            defaultLibraryType={
                isLibraryQuizV2 && !libraryV2Uuid ? defaultLibraryType : undefined
            }
            isAdminQuiz={isAdminQuiz}
            isAdminQuizV2={isAdminQuizV2}
            isMyQuizV2={isMyQuizV2}
            isUserQuiz={isUserQuiz}
        />
    );
};
